import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { Shell } from '../shell.service';
import { NavStateService } from '../nav-state.service';
import { Subject } from 'rxjs';
import { takeUntil, filter, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavMenuComponent implements OnInit, OnDestroy {

  navbar: any[] = [];
  isMobileMenuOpen: boolean = false;
  expandedMobileItems: Set<string> = new Set();
  isMobile: boolean = false;
  private destroy$ = new Subject<void>();
  private isLoadingNavData: boolean = false;
  private readonly resizeHandler = () => this.checkIfMobile();

  constructor(
    public authService: AuthService,
    private service: Shell,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private navStateService: NavStateService
  ) { }

  ngOnInit(): void {
    // Detectar si estamos en móvil
    this.checkIfMobile();
    window.addEventListener('resize', this.resizeHandler);
    
    // Suscribirse al navbar del servicio compartido
    this.navStateService.navbar$
      .pipe(takeUntil(this.destroy$))
      .subscribe(navbar => {
        this.navbar = navbar;
        this.cdr.markForCheck();
      });
    
    // Suscribirse a la señal de recarga
    this.navStateService.reload$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadNavData(true); // Forzar recarga
      });

    // Estrategia: Mostrar caché inmediatamente si existe, luego actualizar en background
    const cachedNavbar = this.navStateService.currentNavbar;
    if (this.authService.isAuthenticated()) {
      if (cachedNavbar.length > 0) {
        // Mostrar caché inmediatamente
        this.navbar = cachedNavbar;
        this.cdr.markForCheck();
        
        // Actualizar en background solo si el caché está cerca de expirar
        if (this.navStateService.isCacheExpiringSoon()) {
          this.loadNavData(false);
        }
      } else {
        // No hay caché, cargar inmediatamente
        this.loadNavData(false);
      }
    }

    // Suscribirse a cambios en el estado de autenticación
    this.authService.authNavStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAuthenticated => {
        if (isAuthenticated && this.navStateService.currentNavbar.length === 0) {
          this.loadNavData(false);
        } else if (!isAuthenticated) {
          // Limpiar inmediatamente el navbar local y el servicio
          this.navbar = [];
          this.navStateService.clearNavbar();
          this.cdr.markForCheck();
        }
      });

    // Detectar cambios de navegación sin recargar el menú
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Solo marcar para detección de cambios, no recargar
        this.cdr.markForCheck();
      });
  }

  loadNavData(forceReload: boolean = false) {
    // Evitar peticiones concurrentes del mismo menú
    if (this.isLoadingNavData) {
      return;
    }

    // Si no es forzado y ya hay datos en cache recientes, no recargar
    if (!forceReload && this.navStateService.currentNavbar.length > 0 && !this.navStateService.isCacheExpiringSoon()) {
      return;
    }

    this.isLoadingNavData = true;

    this.service.getmenu(this.authService.authorizationHeaderValue)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingNavData = false;
        })
      )
      .subscribe((result: any) => {
        if (result && result.length > 0) {
          this.navStateService.setNavbar(result);
        }
      }, error => {
        console.error('Error al cargar el menú:', error);
        // En caso de error, mantener el caché si existe
      });
  }

  trackByItem(index: number, item: any): any {
    return item.Title || item.Route || index;
  }

  onNavigate(route?: string) {
    // 1. Si es móvil, cerramos el menú y limpiamos los submenús expandidos
    if (this.isMobile) {
      this.isMobileMenuOpen = false;
      this.expandedMobileItems.clear(); // Esto limpia los submenús abiertos al navegar
    }

    // 2. Si hay ruta, navegamos
    if (route && route !== '') {
      this.router.navigate([route]);
    }
    
    this.cdr.markForCheck();
  }

  navigate(route: string) {
    if (route && route !== '') {
      this.router.navigate([route]);
      this.isMobileMenuOpen = false;
      this.cdr.markForCheck();
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.cdr.markForCheck();
  }

  async signout() {
    this.isMobileMenuOpen = false;
    this.cdr.markForCheck();
    await this.authService.signout();
  }

  toggleMobileSubmenu(item: any) {
    // Solo funcionar en móvil
    if (!this.isMobile) {
      return;
    }
    
    const itemId = item.Title || item.Route;
    if (this.expandedMobileItems.has(itemId)) {
      this.expandedMobileItems.delete(itemId);
    } else {
      this.expandedMobileItems.add(itemId);
    }
    this.cdr.markForCheck();
  }
  
  checkIfMobile() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;
    
    // Si cambiamos de móvil a desktop, limpiar estados móviles
    if (wasMobile && !this.isMobile) {
      this.isMobileMenuOpen = false;
      this.expandedMobileItems.clear();
      this.cdr.markForCheck();
    }
  }

  isMobileSubmenuExpanded(item: any): boolean {
    const itemId = item.Title || item.Route;
    return this.expandedMobileItems.has(itemId);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
    this.destroy$.next();
    this.destroy$.complete();
  }

}
